import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ItemCard = ({ 
  item, 
  participants, 
  onClaimItem, 
  onReleaseItem, 
  onAdjustQuantity,
  currentUserId = '1' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getClaimedQuantity = () => {
    return item?.claims?.reduce((total, claim) => total + claim?.quantity, 0) || 0;
  };

  const getRemainingQuantity = () => {
    return item?.quantity - getClaimedQuantity();
  };

  const getUserClaim = () => {
    return item?.claims?.find(claim => claim?.userId === currentUserId);
  };

  const isShared = item?.shared && Array.isArray(item?.sharedWith) && item.sharedWith.length > 0;

  // amount of item units that corresponds to a single share (1/N)
  const perShare = isShared ? (item.quantity / item.sharedWith.length) : null;

  const getClaimingUsers = () => {
    return item?.claims?.map(claim => {
      const participant = participants?.find(p => p?.id === claim?.userId);
      const claimedQuantity = claim?.quantity || 0;
      const claimedShares = isShared ? Math.round((claimedQuantity / perShare) * 100) / 100 : null;
      return { ...participant, claimedQuantity, claimedShares };
    }) || [];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(price);
  };

  const getItemStatus = () => {
    const remaining = getRemainingQuantity();
    const claimed = getClaimedQuantity();
    
    if (remaining === 0) return 'fully-claimed';
    if (claimed > 0) return 'partially-claimed';
    return 'available';
  };

  const getStatusColor = () => {
    switch (getItemStatus()) {
      case 'fully-claimed':
        return 'border-success bg-success/5';
      case 'partially-claimed':
        return 'border-warning bg-warning/5';
      case 'available':
        return 'border-border bg-card';
      default:
        return 'border-border bg-card';
    }
  };

  const handleClaim = () => {
    if (isShared) {
      // claim 1 share (parent will convert to actual quantity)
      const otherShares = getClaimingUsers().filter(c => c.id !== currentUserId)
        .reduce((sum, c) => sum + (c.claimedShares || 0), 0) || 0;
      const totalShares = item.sharedWith.length;
      const currentShares = getUserClaim() ? Math.round(((getUserClaim().quantity || 0) / perShare) * 100) / 100 : 0;
      const maxForUser = Math.max(0, Math.round((totalShares - otherShares) * 100) / 100);
      if (currentShares < maxForUser) {
        onClaimItem(item?.id, 1);
      }
    } else {
      const remaining = getRemainingQuantity();
      if (remaining > 0) {
        onClaimItem(item?.id, Math.min(1, remaining));
      }
    }
  };

  const handleRelease = () => {
    const userClaim = getUserClaim();
    if (userClaim) {
      onReleaseItem(item?.id, userClaim?.quantity);
    }
  };

  const handleQuantityChange = (newSharesOrQuantity) => {
    const userClaim = getUserClaim();
    if (isShared) {
      const newShares = newSharesOrQuantity; // number of parts
      const newQuantity = Math.round((newShares * perShare) * 100) / 100;
      const currentQuantity = userClaim?.quantity || 0;
      const difference = newQuantity - currentQuantity;

      if (difference > 0) {
        // ensure available shares remain
        const otherShares = getClaimingUsers().filter(c => c.id !== currentUserId)
          .reduce((sum, c) => sum + (c.claimedShares || 0), 0) || 0;
        const totalShares = item.sharedWith.length;
        const maxForUser = Math.max(0, Math.round((totalShares - otherShares) * 100) / 100);
        if (newShares <= maxForUser) onAdjustQuantity(item?.id, newQuantity);
      } else {
        // allow decreasing to 0
        onAdjustQuantity(item?.id, newQuantity);
      }
    } else {
      const newQuantity = newSharesOrQuantity;
      const currentQuantity = userClaim?.quantity || 0;
      const difference = newQuantity - currentQuantity;

      if (difference > 0 && getRemainingQuantity() >= difference) {
        onAdjustQuantity(item?.id, newQuantity);
      } else if (difference < 0) {
        onAdjustQuantity(item?.id, newQuantity);
      }
    }
  };

  const userClaim = getUserClaim();
  const claimingUsers = getClaimingUsers();
  const remaining = getRemainingQuantity();

  return (
    <div className={`border rounded-lg p-4 transition-all duration-200 ${getStatusColor()}`}>
      {/* Item Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-foreground mb-1">
            {item?.name}
          </h3>
          {item?.description && (
            <p className="text-sm text-text-secondary mb-2">
              {item?.description}
            </p>
          )}
          <div className="flex items-center space-x-3 text-sm">
            <span className="font-semibold text-foreground">
              {formatPrice(item?.price)}
            </span>
            <span className="text-text-secondary">
              Cantidad: {item?.quantity}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          {/* Status Badge */}
          <div className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${getItemStatus() === 'fully-claimed' ? 'bg-success text-success-foreground' :
              getItemStatus() === 'partially-claimed' ? 'bg-warning text-warning-foreground' :
              'bg-muted text-muted-foreground'}
          `}>
            {getItemStatus() === 'fully-claimed' ? 'Completo' :
             getItemStatus() === 'partially-claimed' ? 'Parcial' : 'Disponible'}
          </div>

          {/* Remaining Quantity */}
          {remaining > 0 && (
            <span className="text-xs text-text-secondary">
              {remaining} disponible{remaining !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {/* Claiming Users */}
      {claimingUsers?.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Users" size={14} className="text-text-secondary" />
            <span className="text-xs text-text-secondary">Reclamado por:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {claimingUsers?.map((user) => (
              <div
                key={user?.id}
                className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
              >
                <div className="w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                  {user?.name?.charAt(0)}
                </div>
                <span>{user?.name}</span>
                <span className="font-medium">({isShared ? ( (Math.abs((user?.claimedShares || 0) - Math.round(user?.claimedShares || 0)) < 0.01) ? Math.round(user?.claimedShares || 0) : (Math.round((user?.claimedShares || 0) * 100) / 100) ) + ' / ' + item.sharedWith.length : (user?.claimedQuantity) })</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* User Actions */}
      <div className="flex items-center justify-between">
        {userClaim ? (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-foreground">
              Tu cantidad: <span className="font-medium">
                {isShared ? (
                  (() => {
                    const sharesRaw = (userClaim?.quantity || 0) / perShare || 0;
                    const rounded = Math.round(sharesRaw * 100) / 100;
                    const display = Math.abs(rounded - Math.round(rounded)) < 0.01 ? Math.round(rounded) : rounded;
                    return `${display} / ${item.sharedWith.length}`;
                  })()
                ) : (
                  userClaim?.quantity
                )}
              </span>
            </span>
            <div className="flex items-center space-x-1">
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  if (isShared) {
                    const curr = getUserClaim() ? Math.round(((getUserClaim().quantity || 0) / perShare) * 100) / 100 : 0;
                    handleQuantityChange(Math.max(0, curr - 1));
                  } else {
                    handleQuantityChange(userClaim?.quantity - 1);
                  }
                }}
                disabled={userClaim?.quantity <= 0}
                iconName="Minus"
                iconSize={12}
              />
              <span className="w-8 text-center text-sm font-medium">
                {isShared ? (getUserClaim() ? Math.round(((getUserClaim().quantity || 0) / perShare) * 100) / 100 : 0) : userClaim?.quantity}
              </span>
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  if (isShared) {
                    const curr = getUserClaim() ? Math.round(((getUserClaim().quantity || 0) / perShare) * 100) / 100 : 0;
                    handleQuantityChange(curr + 1);
                  } else {
                    handleQuantityChange(userClaim?.quantity + 1);
                  }
                }}
                disabled={remaining <= 0}
                iconName="Plus"
                iconSize={12}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClaim}
              disabled={remaining <= 0}
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
            >
              Reclamar
            </Button>
          </div>
        )}

        {userClaim && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRelease}
            iconName="X"
            iconSize={14}
            className="text-error hover:text-error"
          >
            Liberar
          </Button>
        )}
      </div>
      {/* Expandable Details */}
      {item?.allergens && item?.allergens?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            iconSize={14}
            className="text-xs text-text-secondary"
          >
            {isExpanded ? 'Ocultar' : 'Ver'} alérgenos
          </Button>
          
          {isExpanded && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-1">
                {item?.allergens?.map((allergen, index) => (
                  <span
                    key={index}
                    className="bg-warning/20 text-warning-foreground px-2 py-1 rounded text-xs"
                  >
                    {allergen}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ItemCard;